/**
 * Column reconciliation helper for the shared Manage Columns system.
 *
 * The columns slice stores a single ordered `allColumns` array per `tableType`
 * (static + custom columns interleaved). The user's column ORDER lives in that
 * array's sequence and is persisted via redux-persist.
 *
 * The bug this helper fixes: `initializeColumns` used to rebuild `allColumns`
 * straight from the code-defined `settings` order on every table mount, throwing
 * away the user's persisted order. `reconcileColumns` instead merges the persisted
 * order with the current code/backend definitions so that:
 *
 *   - persisted state controls ORDER (and, via the slice, visibility),
 *   - current definitions control behavioral metadata (labels, tooltips, render
 *     functions, formatters, permissions, mandatory/terminal status, datakey),
 *   - newly introduced columns are appended after the existing configurable
 *     columns (and before terminal columns) without disturbing the saved order,
 *   - columns that no longer exist are dropped,
 *   - terminal/pinned columns are snapped to their required trailing position,
 *   - malformed persisted state (nulls, missing keys, duplicates) is handled
 *     safely without crashing or duplicating columns.
 *
 * It is a PURE function — the reducer remains responsible for assigning the
 * result to the correct `tableType` and for visibility bookkeeping.
 */

const KEY = "datakey";

const getKey = (col) => (col == null ? undefined : col[KEY]);

const isValidColumn = (col) =>
  col != null && typeof col === "object" && getKey(col) != null;

/**
 * Move terminal/pinned columns (e.g. "add_comment") to the end, preserving the
 * relative order of everything else. Mirrors the slice's reorderColumnsInternally.
 */
export const enforceTerminalOrder = (columns, terminalColumnKeys = []) => {
  const terminalSet = new Set(terminalColumnKeys);
  const nonTerminal = [];
  const terminal = [];

  columns.forEach((col) => {
    if (terminalSet.has(getKey(col))) {
      terminal.push(col);
    } else {
      nonTerminal.push(col);
    }
  });

  return [...nonTerminal, ...terminal];
};

/**
 * Reconcile a persisted column order with the current set of column definitions.
 *
 * @param {Object}   params
 * @param {Array}    params.currentColumns       Authoritative current definitions,
 *                                               in their default (code/backend) order.
 * @param {Array}    params.persistedColumns     Previously persisted `allColumns`
 *                                               (the user's saved order; may include
 *                                               custom columns).
 * @param {string[]} [params.terminalColumnKeys] Datakeys forced to the trailing
 *                                               position regardless of saved order.
 * @param {boolean}  [params.preserveCustom]     When true, persisted custom columns
 *                                               that are absent from `currentColumns`
 *                                               are retained (used during
 *                                               initializeColumns, where custom defs
 *                                               are not part of `settings`). When
 *                                               false, anything absent from
 *                                               `currentColumns` is dropped (used
 *                                               during setCustomColumns, which is
 *                                               authoritative for the custom set).
 * @returns {Array} The reconciled, de-duplicated, terminal-ordered column array.
 */
export const reconcileColumns = ({
  currentColumns,
  persistedColumns,
  terminalColumnKeys = [],
  preserveCustom = false,
} = {}) => {
  const current = Array.isArray(currentColumns)
    ? currentColumns.filter(isValidColumn)
    : [];
  const persisted = Array.isArray(persistedColumns)
    ? persistedColumns.filter(isValidColumn)
    : [];

  // Current definitions keyed by datakey (last definition wins on duplicate keys).
  const currentByKey = new Map();
  current.forEach((col) => currentByKey.set(getKey(col), col));

  // Persisted columns keyed by datakey (first occurrence wins → de-dupes safely).
  const persistedByKey = new Map();
  persisted.forEach((col) => {
    const key = getKey(col);
    if (!persistedByKey.has(key)) persistedByKey.set(key, col);
  });

  // 1. Existing columns: keep persisted order, refresh definition from current code.
  //    A column is "existing" if it's still defined in current, OR (when
  //    preserveCustom) it is a persisted custom column we must carry forward.
  const seen = new Set();
  const reconciledExisting = [];
  persisted.forEach((col) => {
    const key = getKey(col);
    if (seen.has(key)) return; // drop duplicate persisted entries
    const hasCurrentDef = currentByKey.has(key);
    const keepAsCustom = preserveCustom && col.isCustom === true;
    if (!hasCurrentDef && !keepAsCustom) return; // stale → drop

    seen.add(key);
    // Persisted entry first (user-configurable bits), current definition wins for
    // behavioral metadata (label, tooltip, render, permissions, mandatory, etc.).
    const merged = hasCurrentDef
      ? { ...persistedByKey.get(key), ...currentByKey.get(key) }
      : persistedByKey.get(key);
    reconciledExisting.push(merged);
  });

  // 2. New columns: present in current defs but not persisted → append in current
  //    (default) order, after the existing configurable columns.
  const newColumns = [];
  current.forEach((col) => {
    const key = getKey(col);
    if (persistedByKey.has(key) || seen.has(key)) return;
    seen.add(key);
    newColumns.push(col);
  });

  // 3. Snap terminal/pinned columns back to the end.
  return enforceTerminalOrder(
    [...reconciledExisting, ...newColumns],
    terminalColumnKeys
  );
};

export default reconcileColumns;
